/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2021. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { defineMessages } from 'react-intl';

export default defineMessages({
  title: {
    id: 'YourBenefits_Title',
    defaultMessage: 'Your Benefits',
  },
   yourBenefitAccountTitle: {
    id: 'Account_YourBenefitAccountTitle',
    defaultMessage: 'Your Account',
  },
  applyButton: {
    id: 'YourBenefits_ApplyButton',
    defaultMessage: 'Apply',
  },
  applicationInProgressBadge: {
    id: 'YourBenefits_ApplicationInProgressBadge',
    defaultMessage: 'Application in progress',
  },
  lastUpdatedText: {
    id: 'YourBenefits_LastUpdatedText',
    defaultMessage: 'Updated {date}',
  },
  applicationInProgressText: {
    id: 'YourBenefits_ApplicationInProgressText',
    defaultMessage: 'Your application is in progress since {date}.',
  },
  continue: {
    id: 'YourBenefits_Continue',
    defaultMessage: 'Continue',
  },
  delete: {
    id: 'YourBenefits_Delete',
    defaultMessage: 'Delete application',
  },
  withdraw: {
    id: 'YourBenefits_Withdraw',
    defaultMessage: 'Withdraw',
  },
  cancel: {
    id: 'YourBenefits_Cancel',
    defaultMessage: 'Cancel',
  },
  viewSubmittedApplication: {
    id: 'YourBenefits_ViewSubmittedApplication',
    defaultMessage: 'View your application \u200F(PDF)',
  },
  viewSubmittedApplicationAriaLabel: {
    id: 'YourBenefits_ViewSubmittedApplicationAriaLabel',
    defaultMessage: 'View your application PDF for {name}',
  },
  moreInfo: {
    id: 'YourBenefits_MoreInfo',
    defaultMessage: 'More info',
  },
  noBenefitsTitle: {
    id: 'YourBenefits_NoBenefitsTitle',
    defaultMessage: 'Applying for benefits',
  },
  // BEGIN PR-7541 Staten - Updating body text on Your Benfits page for when no applications have yet been submitted
  noBenefitsBody: {
    id: 'YourBenefits_NoBenefitsBody',
    defaultMessage:
      'Benefits you are applying for and application status will be shown here. Paper applications are not shown, but contact us if you have any questions.',
  },
  // END PR-7541 Staten - Updating body text on Your Benfits page for when no applications have yet been submitted
  applyForBenefitsLink: {
    id: 'YourBenefits_ApplyForBenefitsLink',
    defaultMessage: 'Apply for benefits',
  },
  allPayments: {
    id: 'YourBenefits_AllPayments',
    defaultMessage: 'All payments',
  },
  deleteDialogTitle: {
    id: 'YourBenefits_DeleteDialogTitle',
    defaultMessage: 'Delete this application?',
  },
  deleteDialogBody: {
    id: 'YourBenefits_DeleteDialogBody',
    defaultMessage: 'This deletes the information you have given in this application.',
  },
  applyForBenefitsDescription: {
    id: 'YourBenefits_ApplyForBenefitsDescription',
    defaultMessage: 'Start your application now.',
  },
  pendingDecisionStatus: {
    id: 'YourBenefits_PendingDecisionStatus',
    defaultMessage: 'Pending Decision',
  },
  ActiveStatus: {
    id: 'YourBenefits_ActiveStatus',
    defaultMessage: 'Active',
  },
  deletingApplicationErrorMessage: {
    id: 'DeleteApplication_ErrorMessage',
    defaultMessage: 'An error occurred when deleting the application.',
  },
  failureToastText: {
    id: 'SubmittedApplicationPDFDownloadFailureToast_Message',
    defaultMessage: "Can't get your application right now. Please try later.",
  },
  // BEGIN PR-7541 Staten - Updating "Active"/"Denied" values to both be "Completed" for Application status
  completedStatus: {
    id: 'YourBenefits_CompletedStatus',
    defaultMessage: 'Completed',
  },
  // END PR-7541 Staten - Updating "Active"/"Denied" values to both be "Completed" for Application status

  // BEGIN PR-12516 - SW - Verbiage for renewal status panel
  onlineRenewal: {
    id: 'YourBenefits_OnlineRenewal',
    defaultMessage: 'Online Renewal',
  },
  onlineRenewalsStart: {
    id: 'YourBenefits_OnlineRenewalsStart',
    defaultMessage: 'Start',
  },
  onlineRenewalsContinue: {
    id: 'YourBenefits_OnlineRenewalsContinue',
    defaultMessage: 'Continue',
  },
  onlineRenewalsInstructText: {
    id: 'YourBenefits_OnlineRenewalsInstructText',
    defaultMessage:
      'Click the Start button to complete the online renewal form for the {program} by {date}. This will aid us in determining if the individuals listed below will be eligible to receive medical benefits for a new period. ',
  },
  // BEGIN PR-12516 - SW - Verbiage for renewal status panel
  deadlineDateText: {
    id: 'YourBenefits_DeadlineDateText',
    defaultMessage: 'deadline {deadlineDate}',
  },
  notCompletedStatus: {
    id: 'YourBenefits_NotCompletedStatus',
    defaultMessage: 'Not Completed',
  },
  pendingVerificationsStatus: {
    id: 'YourBenefits_PendingVerificationsStatus',
    defaultMessage: 'Pending Verifications',
  },
  pastDueStatus: {
    id: 'YourBenefits_PastDueStatus',
    defaultMessage: 'Past Due',
  },
  existingCoverageMessage: {
    id: 'YourBenefits_ExistingCoverageMessage',
    defaultMessage: 'Eligible for coverage from {startDate} through {endDate}',
  },
  expiredCoverageMessage: {
    id: 'YourBenefits_ExpiredCoverageMessage',
    defaultMessage: 'No coverage since {lastEndDate}',
  },
  noCoverageMessage: {
    id: 'YourBenefits_NoCoverageMessage',
    defaultMessage: 'No coverage',
  },
  linkMyAccountLink: {
    id: 'YourBenefits_LinkMyAccountLink',
    defaultMessage: 'Link My Account',
  },
  renewalStatusTitle: {
    id: 'YourBenefits_RenewalStatusTitle',
    defaultMessage: 'Renewal Status',
  },
  /** START SD-6497 update verbiage */
  coverageInformationTitle: {
    id: 'YourBenefits_CoverageInformationTitle',
    defaultMessage: 'Medicaid/Medical Coverage Information',
  },
  ccaCoverageInformationTitle: {
    id: 'YourBenefits_CCACoverageInformationTitle',
    defaultMessage: 'Child Care Assistance Coverage Information',
  },
  noCoverageInformationMessage: {
    id: 'YourBenefits_NoCoverageInformationMessage',
    defaultMessage: 'No coverage information available',
  },
  /** END SD-6497 update verbiage */
  applicationTitle: {
    id: 'YourBenefits_ApplicationTitle',
    defaultMessage: 'Application for the Government Health Plan',
  },
  renewalTitle: {
    id: 'YourBenefits_RenewalTitle',
    defaultMessage: 'Renewal for the Government Health Plan',
  },
  cocTitle: {
    id: 'YourBenefits_CoCTitle',
    defaultMessage: 'Change of Circumstances for the Government Health Plan',
  },
  retroTitle: {
    id: 'YourBenefits_RetroTitle',
    defaultMessage: 'Retroactive Application for the Government Health Plan',
  },
  withdrawnStatus: {
    id: 'YourBenefits_WithdrawnStatus',
    defaultMessage: 'Withdrawn',
  },
  // BEGIN PR-12848 ZPK - online renewal continue text
  onlineRenewalsContinueInstructText: {
    id: 'YourBenefits_OnlineRenewalsContinueInstructText',
    defaultMessage:
      'Click the Continue button to complete the online renewal form for the {program} by {date}.',
  },
  // END PR-12848 ZPK - online renewal continue text

  governmentHealthPlanRetroTitle: {
    id: 'YourBenefits_GovernmentHealthPlanRetroTitle',
    defaultMessage: 'Retroactive Government Health Plan',
  },
  // BEGIN SD-1058 - Staten - adding alternative text for SNAP Applications
  receivedText: {
    id: 'YourBenefits_ReceivedText',
    defaultMessage: 'Received {date}',
  },
  snapSubmittedText: {
    id: 'YourBenefits_SNAPSubmittedText',
    defaultMessage:
      'We have received your application. Please contact your local office for the status of your application.',
  },
  // END SD-1058 - Staten - adding alternative text for SNAP Applications
  // BEGIN - SD-1058 - Staten - adding alternative text for Resource Assessment applications
  resourceAssessmentBodyText: {
    id: 'YourBenefits_ResourceAssessmentBodyText',
    defaultMessage:
      'We have received your Resource Assessment. Please contact your local office for the status.',
  },
  viewSubmittedResourceAssessment: {
    id: 'YourBenefits_ViewSubmittedResourceAssessment',
    defaultMessage: 'View your Resource Assessment \u200F(PDF)',
  },
  // END - SD-1058 - Staten - adding alternative text for Resource Assessment applications
});
